import { Link } from 'react-router-dom';

import { MediaThumbnail } from '@/components/media-thumbnail';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { type MediaItem, mediaSizeLabel } from '@/lib/media';

interface MediaDetailDialogProps {
  media: MediaItem | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** Read-only viewer for a media asset: preview, metadata and linked usages. */
export function MediaDetailDialog({ media, open, onOpenChange }: MediaDetailDialogProps) {
  if (!media) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{media.title || media.filename}</DialogTitle>
          <DialogDescription className="truncate">{media.filename}</DialogDescription>
        </DialogHeader>

        <MediaThumbnail item={media} className="aspect-video w-full rounded-md border" />

        <dl className="grid grid-cols-2 gap-3 text-sm">
          <div className="space-y-0.5">
            <dt className="text-muted-foreground text-xs">Type</dt>
            <dd className="capitalize">{media.type}</dd>
          </div>
          <div className="space-y-0.5">
            <dt className="text-muted-foreground text-xs">Size</dt>
            <dd>{mediaSizeLabel(media)}</dd>
          </div>
          <div className="space-y-0.5">
            <dt className="text-muted-foreground text-xs">MIME type</dt>
            <dd>{media.mimeType}</dd>
          </div>
          <div className="space-y-0.5">
            <dt className="text-muted-foreground text-xs">Updated</dt>
            <dd>{media.updated}</dd>
          </div>
        </dl>

        <div className="space-y-2">
          <p className="font-medium text-sm">Usages</p>
          {media.usages.length === 0 ? (
            <p className="text-muted-foreground text-sm">Not used anywhere yet.</p>
          ) : (
            <ul className="divide-y rounded-md border">
              {media.usages.map((usage) => (
                <li
                  key={usage.id}
                  className="flex items-center justify-between gap-3 px-3 py-2 text-sm"
                >
                  <Link
                    to={
                      usage.kind === 'project'
                        ? `/projects/${usage.slug}`
                        : `/proposals/${usage.slug}`
                    }
                    className="font-medium hover:underline"
                    onClick={() => onOpenChange(false)}
                  >
                    {usage.label}
                  </Link>
                  <span className="flex items-center gap-2 text-muted-foreground text-xs">
                    <span className="capitalize">{usage.kind}</span>
                    <Badge
                      variant="outline"
                      className="border-transparent bg-muted text-muted-foreground"
                    >
                      {usage.role}
                    </Badge>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <DialogFooter className="items-center justify-between gap-2 pt-2 sm:justify-between">
          <a
            href={media.url}
            target="_blank"
            rel="noreferrer"
            className="text-muted-foreground text-xs underline underline-offset-4 hover:text-foreground"
          >
            Open original
          </a>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
